import React from 'react';

import {
  Box,
  Text,
  Input,
  FormControl,
  FormLabel,
  VStack,
  InputGroup,
  InputLeftAddon,
  Textarea,
} from '@chakra-ui/react';

import { useColors } from './colors';

const RawSupplierInventory = ({ listing }) => {
  const {
    bgColor,
    borderColor,
    headingColor,
    subTextColor: labelColor,
    hoverBg: readOnlyBg,
    inputColor: readOnlyColor,
  } = useColors();

  // Convert supplier prices array to JSON object
  const formatSupplierPrices = React.useMemo(() => {
    if (!listing?.supplier?.prices || !Array.isArray(listing.supplier.prices)) {
      return JSON.stringify({}, null, 2);
    }

    const pricesObj = {};
    listing.supplier.prices.forEach((priceItem) => {
      if (priceItem.key && priceItem.value !== undefined) {
        pricesObj[priceItem.key] = priceItem.value;
      }
    });

    return JSON.stringify(pricesObj, null, 2);
  }, [listing?.supplier?.prices]);

  const rawSupplierData = {
    prices: formatSupplierPrices,
    availableQuantity: listing?.supplier?.stock?.toString() || '0',
    externalShippingCost:
      listing?.supplier?.syncedDetails?.externalProvidedShippingCost?.toString() ||
      '0.00',
    appliedFormula:
      listing?.supplier?.syncedDetails?.appliedSellingPriceFormula ||
      'No formula available',
  };

  return (
    <Box
      bg={bgColor}
      borderRadius="xl"
      p={6}
      border="1px solid"
      borderColor={borderColor}
      w="600px"
      minH="100%"
    >
      <Text fontSize="xl" fontWeight="semibold" color={headingColor} mb={6}>
        Raw Supplier Item Inventory
      </Text>

      <VStack spacing={5} align="stretch">
        {/* Prices JSON */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
            Prices
          </FormLabel>
          <Textarea
            value={rawSupplierData.prices}
            isReadOnly
            minRows={10}
            minH="150px"
            resize="vertical"
            fontFamily="monospace"
            fontSize="sm"
            borderRadius="md"
            _focus={{
              borderColor: 'blue.400',
              boxShadow: '0 0 0 1px #3182ce',
            }}
            bg={readOnlyBg}
            color={readOnlyColor}
          />
        </FormControl>

        {/* Available Quantity */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
            Available Quantity
          </FormLabel>
          <InputGroup>
            <InputLeftAddon children="#" />
            <Input
              value={rawSupplierData.availableQuantity}
              isReadOnly
              size="md"
              borderRadius="md"
              bg={readOnlyBg}
              color={readOnlyColor}
            />
          </InputGroup>
        </FormControl>

        {/* External Provided Shipping Cost */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
            External Provided Shipping Cost
          </FormLabel>
          <InputGroup>
            <InputLeftAddon children="$" />
            <Input
              value={rawSupplierData.externalShippingCost}
              isReadOnly
              bg={readOnlyBg}
              color={readOnlyColor}
              size="md"
              borderRadius="md"
            />
          </InputGroup>
        </FormControl>

        {/* Applied Selling Price Formula */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
            Applied Selling Price Formula
          </FormLabel>
          <Input
            value={rawSupplierData.appliedFormula}
            isReadOnly
            bg={readOnlyBg}
            color={readOnlyColor}
            size="md"
            borderRadius="md"
            fontFamily="monospace"
            fontSize="sm"
          />
        </FormControl>
      </VStack>
    </Box>
  );
};

export default RawSupplierInventory;
